/**
 * ResultDefaultStatusIcon renders the default icon for the current Result status.
 * It uses the Result context to determine status and size, and applies the correct color.
 */
import type { SxProps } from '@mui/material';
import { SvgIcon } from '@mui/material';
import React from 'react';
import { DATA_TEST } from './enums';
import { useResultContext } from './ResultContext';
import { getStatusIconAndColor } from './utils/status';

/**
 * Props for ResultDefaultStatusIcon.
 */
export interface ResultDefaultStatusIconProps {
  /** Custom styles for the icon container */
  sx?: SxProps;
  /** Test id for testing (MUI convention: dataTest, renders as data-test) */
  dataTest?: string;
}

/**
 * Renders the default status icon for the current Result context.
 */
export const ResultDefaultStatusIcon: React.FC<ResultDefaultStatusIconProps> = ({ sx, dataTest }) => {
  const { status } = useResultContext();
  const { icon: IconComponent, color } = getStatusIconAndColor(status);
  return (
    <SvgIcon
      component={IconComponent}
      inheritViewBox
      sx={{ color, ...sx }}
      data-test={dataTest ?? DATA_TEST.result_default_status_icon}
    />
  );
};
